import { Button, InputText, Text } from "@pentabd/ui";
import SelectProductsTable from "./SelectProductsTable";
import { useTranslation } from "react-i18next";
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import {
  DemandCreateType,
  SingleDemandResponseType,
} from "@type/procurements/demand";
import {
  CancelButton,
  ErrorModal,
  MultiFileRow,
  StatusBadge,
  SuccessModal,
} from "@components";
import { useEffect, useState } from "react";
import useActionCreator from "@redux/hooks/useActionCreator";
import { useNavigate } from "react-router-dom";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import { ROUTES } from "@constants/routes";
import { SelectedProductTable } from "./SecondaryTable/SelectedProductsTable";
import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";

type SelectedProductType = {
  [key: string]: any;
  approximateUnitPrice: number;
  quantity: number;
};

const CreateProcurementDemandForm = ({
  demand,
  id,
}: {
  demand: SingleDemandResponseType | null;
  id: string | undefined;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { demandCreateActionHandler } = useActionCreator();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { createSuccess, createError } = useCustomSelector(
    (store) => store.procurements.demandState,
  );

  const [selectedProduct, setSelectedProduct] = useState<
    SelectedProductType[] | []
  >([]);

  const selectProductHandler = (data: any) => {
    setSelectedProduct((prev: SelectedProductType[]) => {
      let index = prev.findIndex((item: any) => item?.id === data?.id);

      if (index >= 0) {
        return prev;
      } else {
        return [
          ...prev,
          { ...data, approximateUnitPrice: 0, quantity: 1, itemUnit: "" },
        ];
      }
    });
  };

  // const onChangeHandler = (
  //   id: number,
  //   name: string,
  //   value: number | string,
  // ) => {
  //   setSelectedProduct((prev) => {
  //     const index = selectedProduct.findIndex((item: any) => item?.id === id);

  //     if (index >= 0) {
  //       prev[index][`${name}`] = value;
  //     }

  //     return prev;
  //   });
  // };

  // const onEditorStateChange = (editorState: any) => {
  //   setValue("letter", editorState, {
  //     shouldValidate: true,
  //     shouldDirty: true,
  //   });
  // };

  const methods = useForm<DemandCreateType>({
    values: revertMultipleDocumentData(demand, [
      "documents",
    ]) as SingleDemandResponseType,
  });

  const {
    register,
    reset,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues,
  } = methods;

  const { fields, append } = useFieldArray({
    control,
    name: "items",
  });

  const onSubmit: SubmitHandler<DemandCreateType> = (
    data: DemandCreateType,
  ) => {
    const modifiedProductData = data?.items?.map((item: any) => {
      const { id, approximateUnitPrice, quantity, itemUnit, isAsset } = item;
      return {
        id,
        approximateUnitPrice: Number(approximateUnitPrice),
        quantity: Number(quantity),
        itemUnit,
        isAsset,
      };
    });

    if (data?.items.length > 0) {
      setIsErrorModalOpen(false);
      demandCreateActionHandler({
        ...convertMultipleDocumentData(data, ["documents"]),
        // items: modifiedProductData,
        status: "pending",
      });
    } else {
      setIsErrorModalOpen(true);
    }
  };

  const createSuccessAction = () => {
    reset();
    setIsSuccessModalOpen(false);
    navigate(`${ROUTES.PROCUREMENT.DEMANDS}`);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  useEffect(() => {
    if (createSuccess) {
      setIsSuccessModalOpen(true);
    }

    if (createError) {
      setIsErrorModalOpen(true);
    }
  }, [createSuccess, createError]);

  useEffect(() => {
    fields?.forEach((item: any) => {
      append(item);
    });
  }, [selectedProduct, append]);

  return (
    <div>
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
          subtitle="You have created a procurement demand"
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          subtitle={
            createError?.response?.data?.message ||
            "please select products" ||
            "something was wrong"
          }
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
      {/* modals end */}

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="container p-10">
            <div className="d-flex flex-column gap-10 pb-10 border-bottom">
              <div className="row">
                <div className="col-md-12 col-lg-3 d-flex">
                  <Text weight="semibold" color="title" size="sm" component="p">
                    {t("PROCUREMENT_DEMAND.FORM.DEMAND_NAME.LABEL")}
                  </Text>
                  {!id && <span className="text-danger">&#42;</span>}
                </div>
                <div className="col-md-12 col-lg-7">
                  <InputText
                    type="text"
                    {...register("name", {
                      required: "Demand name is required",
                    })}
                    error={errors as any}
                    placeholder={t(
                      "PROCUREMENT_DEMAND.FORM.DEMAND_NAME.PLACEHOLDER",
                    )}
                    disabled={id ? true : false}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 col-lg-3 d-flex">
                  <Text weight="semibold" color="title" size="sm" component="p">
                    {t("PROCUREMENT_DEMAND.FORM.DEMAND_NO.LABEL")}
                  </Text>
                  {!id && <span className="text-danger">&#42;</span>}
                </div>
                <div className="col-md-12 col-lg-7">
                  <InputText
                    type="number"
                    {...register("demandRefNo", {
                      required: "Demand name is required",
                    })}
                    error={errors as any}
                    placeholder={t(
                      "PROCUREMENT_DEMAND.FORM.DEMAND_NO.PLACEHOLDER",
                    )}
                    disabled={id ? true : false}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 col-lg-3 d-flex">
                  <Text weight="semibold" color="title" size="sm" component="p">
                    {t("PROCUREMENT_DEMAND.FORM.APPROX_COST.LABEL")}
                  </Text>
                  {!id && <span className="text-danger">&#42;</span>}
                </div>
                <div className="col-md-12 col-lg-7">
                  <InputText
                    type="number"
                    {...register("totalApproximateCost", {
                      required: "Total cost is required",
                      valueAsNumber: true,
                    })}
                    error={errors as any}
                    placeholder={t(
                      "PROCUREMENT_DEMAND.FORM.APPROX_COST.PLACEHOLDER",
                    )}
                    disabled={id ? true : false}
                  />
                </div>
              </div>

              {id && (
                <div className="row">
                  <div className="col-md-12 col-lg-3 d-flex">
                    <Text
                      weight="semibold"
                      color="title"
                      size="sm"
                      component="p"
                    >
                      {t("PROCUREMENT_DEMAND.FORM.STATUS.LABEL")}
                    </Text>
                  </div>
                  <div className="col-md-12 col-lg-7">
                    <StatusBadge status={demand?.status || ""} />
                  </div>
                </div>
              )}

              <MultiFileRow
                label="PROCUREMENT_DEMAND.FORM.UPLOAD_DOCUMENT"
                name={"documents"}
                control={control}
                setValue={setValue}
                register={register}
                required
                error={errors}
                disabled={id ? true : false}
                fileDetails={["documentName", "date", "remarks", "memorialNo"]}
              />

              {/* <div className="col-12 p-0 mt-10">
                <ReactQuill
                  theme="snow"
                  value={getValues("letter")}
                  onChange={onEditorStateChange}
                  readOnly={id ? true : false}
                />
              </div> */}

              <SelectedProductTable
                data={id ? demand?.items : selectedProduct}
                disable={id ? true : false}
              />

              {!id && (
                <SelectProductsTable
                  selectedProduct={selectedProduct}
                  selectProductHandler={selectProductHandler}
                />
              )}
            </div>
            {!id && (
              <div className="col-12 d-flex justify-content-end mt-10 gap-8">
                <CancelButton />
                <Button fill="fill" type="primary" htmlType="submit" size="sm">
                  {t("PROCUREMENT_DEMAND.DEMAND_CREATE.SUBMIT")}
                </Button>
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default CreateProcurementDemandForm;
